import { StyleSheet } from "react-native";

/**
 * Mobile-only (iPhone) overrides for the My Tickets screen.
 * Applied when Platform.OS === "ios" && windowWidth < 768.
 * Does NOT affect web or iPad.
 */
const mobileMyTicketsStyles = StyleSheet.create({
  /* ── Screen root ─────────────────────────────────────────── */
  screenRoot: {
    flex: 1,
    backgroundColor: "#f0f4fa",
  },

  /* ── Header block (white, full width) ───────────────────── */
  headerBlock: {
    backgroundColor: "#ffffff",
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  backArrowWrap: {
    padding: 2,
  },
  headerTitleBlock: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#1a2332",
    lineHeight: 26,
  },
  headerSubtitle: {
    fontSize: 12,
    color: "#818181",
    marginTop: 1,
  },
  newTicketBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#034895",
    alignItems: "center",
    justifyContent: "center",
  },

  /* ── Search + Filters row ────────────────────────────────── */
  toolbarRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchWrap: {
    flex: 1,
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: 12,
    zIndex: 2,
  },
  searchInput: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 10,
    paddingLeft: 38,
    paddingRight: 12,
    paddingVertical: 10,
    height: 44,
    fontSize: 14,
    color: "#2d2d2d",
  },
  filterBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 10,
    paddingHorizontal: 14,
    height: 44,
  },
  filterBtnText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2d2d2d",
  },
  filterBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#034895",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 5,
  },
  filterBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#ffffff",
  },

  /* ── Ticket Cards ────────────────────────────────────────── */
  cardList: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 10,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#e8e8e8",
    overflow: "hidden",
  },
  cardLeftBorder: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
  },
  cardContent: {
    paddingLeft: 4,
  },
  cardTopRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  ticketId: {
    fontSize: 12,
    fontWeight: "700",
    color: "#005cbc",
    marginBottom: 4,
  },
  ticketTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#1a2332",
    lineHeight: 21,
    marginBottom: 10,
  },
  badgeRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 10,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 100,
    alignSelf: "flex-start",
  },
  badgeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: "700",
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    marginBottom: 4,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  metaText: {
    fontSize: 12,
    color: "#585858",
  },
  actionNeededRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
  },
  actionNeededText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#CC6E16",
  },
  /** "View primary" link shown below the badge for closed-merged cards */
  mergedIntoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
  },
  mergedIntoLink: {
    fontSize: 12,
    fontWeight: "600",
    color: "#7871E2",
    textDecorationLine: "underline",
  },

  /* ── Mobile Pagination ───────────────────────────────────── */
  mobilePaginationCountText: {
    fontSize: 13,
    fontWeight: "500",
    color: "#616161",
    textAlign: "center",
    marginTop: 16,
    marginBottom: 6,
  },
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 16,
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 36,
    minWidth: 36,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationCellDisabled: {
    opacity: 0.4,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
  },

  /* ── Filter Sheet (modal) ────────────────────────────────── */
  sheetOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "flex-end",
  },
  sheetContainer: {
    backgroundColor: "#ffffff",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 34,
    maxHeight: "85%",
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#d0d0d0",
    alignSelf: "center",
    marginTop: 12,
    marginBottom: 8,
  },
  sheetHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  sheetTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: "#1a2332",
  },
  sheetClearAll: {
    fontSize: 14,
    fontWeight: "700",
    color: "#034895",
  },
  /* Dark header variant (used in redesigned filter sheet) */
  sheetHeaderDark: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: "#1a2332",
  },
  filterFullScreen: {
    flex: 1,
    backgroundColor: "#f8f9fa",
  },
  sheetCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "rgba(255,255,255,0.15)",
    alignItems: "center",
    justifyContent: "center",
  },
  sheetTitleDark: {
    fontSize: 18,
    fontWeight: "700",
    color: "#ffffff",
  },
  sheetClearAllDark: {
    fontSize: 14,
    fontWeight: "700",
    color: "#4ea8f5",
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
  },
  filterSection: {
    marginBottom: 22,
  },
  filterSectionLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1a2332",
    letterSpacing: 0.8,
    marginBottom: 12,
  },
  filterChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  filterChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#d0d0d0",
    backgroundColor: "#ffffff",
  },
  filterChipActive: {
    borderColor: "#034895",
    backgroundColor: "#eaf0fb",
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#3a3a3a",
  },
  filterChipTextActive: {
    color: "#034895",
  },
  filterDropdownWrap: {
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 13,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
  },
  filterDropdownText: {
    fontSize: 14,
    color: "#3a3a3a",
  },
  filterDropdownList: {
    marginTop: 4,
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 10,
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  filterDropdownItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    backgroundColor: "#ffffff",
  },
  filterDropdownItemSelected: {
    backgroundColor: "#eaf0fb",
  },
  filterDropdownItemPressed: {
    backgroundColor: "#f3f7fd",
  },
  filterDropdownItemText: {
    fontSize: 14,
    color: "#1a2332",
  },
  filterDropdownItemTextSelected: {
    color: "#034895",
    fontWeight: "700",
  },
  sheetFooter: {
    paddingHorizontal: 20,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#f0f0f0",
  },
  sheetFooterClearAll: {
    paddingVertical: 8,
    marginBottom: 4,
  },
  sheetFooterClearAllText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#374151",
  },
  applyBtn: {
    backgroundColor: "#034895",
    borderRadius: 100,
    paddingVertical: 15,
    alignItems: "center",
  },
  applyBtnPressed: {
    backgroundColor: "#023466",
  },
  applyBtnText: {
    fontSize: 15,
    fontWeight: "700",
    color: "#ffffff",
  },

  /* ── Badge color variants (status & urgency) ─────────────── */
  // Urgency
  urgencyCritical: { backgroundColor: "#FFE5DE" },
  urgencyCriticalDot: { backgroundColor: "#C12C01" },
  urgencyCriticalText: { color: "#C12C01" },
  urgencyHigh: { backgroundColor: "#FFE9D1" },
  urgencyHighDot: { backgroundColor: "#CC6E16" },
  urgencyHighText: { color: "#CC6E16" },
  urgencyMedium: { backgroundColor: "#FEF1B9" },
  urgencyMediumDot: { backgroundColor: "#8B6508" },
  urgencyMediumText: { color: "#8B6508" },
  urgencyLow: { backgroundColor: "#EBEBEB" },
  urgencyLowDot: { backgroundColor: "#585858" },
  urgencyLowText: { color: "#585858" },
  // Status
  statusInProgress: { backgroundColor: "#E1E3FD" },
  statusInProgressDot: { backgroundColor: "#7871E2" },
  statusInProgressText: { color: "#7871E2" },
  statusWaitingonIT: { backgroundColor: "#FEF1B9" },
  statusWaitingonITDot: { backgroundColor: "#8B6508" },
  statusWaitingonITText: { color: "#8B6508" },
  statusEscalatedtoIT: { backgroundColor: "#FEF1B9" },
  statusEscalatedtoITDot: { backgroundColor: "#8B6508" },
  statusEscalatedtoITText: { color: "#8B6508" },
  statusWaitingonUser: { backgroundColor: "#FFE9D1" },
  statusWaitingonUserDot: { backgroundColor: "#CC6E16" },
  statusWaitingonUserText: { color: "#CC6E16" },
  statusWaitingonTMV: { backgroundColor: "#AAD2E2" },
  statusWaitingonTMVDot: { backgroundColor: "#0078A7" },
  statusWaitingonTMVText: { color: "#0078A7" },
  statusEscalatedtoTMV: { backgroundColor: "#AAD2E2" },
  statusEscalatedtoTMVDot: { backgroundColor: "#0078A7" },
  statusEscalatedtoTMVText: { color: "#0078A7" },
  statusSubmittedtoADO: { backgroundColor: "#DCD5FB" },
  statusSubmittedtoADODot: { backgroundColor: "#5C4EC9" },
  statusSubmittedtoADOText: { color: "#5C4EC9" },
  statusClosed: { backgroundColor: "#DDF9DA" },
  statusClosedDot: { backgroundColor: "#027C06" },
  statusClosedText: { color: "#027C06" },
  statusClosedMerged: { backgroundColor: "#E9E9E9" },
  statusClosedMergedDot: { backgroundColor: "#6B7280" },
  statusClosedMergedText: { color: "#6B7280" },
  statusNew: { backgroundColor: "#DAEEFA" },
  statusNewDot: { backgroundColor: "#034895" },
  statusNewText: { color: "#034895" },
  statusResolved: { backgroundColor: "#DDF9DA" },
  statusResolvedDot: { backgroundColor: "#027C06" },
  statusResolvedText: { color: "#027C06" },
});

// Left-border accent colors per status
export const STATUS_BORDER_COLOR: Record<string, string> = {
  "waiting-user": "#FF8C09",
  "waiting-it": "#FCC42B",
  "waiting-tmv": "#0078A7",
  progress: "#7871E2",
  "submitted-ado": "#5C4EC9",
  resolved: "#027C06",
  new: "#034895",
  "closed-merged": "#6B7280",
};

export default mobileMyTicketsStyles;
